import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, CheckCircle2, Clock3, ShieldAlert, ShieldCheck } from "lucide-react";
import { AppHeader } from "@/components/site/AppHeader";
import { Button } from "@/components/ui/button";
import { getMyPartnerVerificationStatus } from "@/lib/partner.functions";

export const Route = createFileRoute("/_authenticated/partner/verification")({
  head: () => ({
    meta: [
      { title: "Verification — Shields Partner" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: PartnerVerificationPage,
});

function PartnerVerificationPage() {
  const query = useQuery({
    queryKey: ["my-partner-verification"],
    queryFn: () => getMyPartnerVerificationStatus(),
    retry: false,
  });

  if (query.isLoading) {
    return (
      <div className="min-h-screen bg-background pb-28">
        <AppHeader workspace="partner" />
        <main className="mx-auto w-full max-w-3xl px-4 py-8 text-sm text-muted-foreground">
          Checking your verification status…
        </main>
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div className="min-h-screen bg-background pb-28">
        <AppHeader workspace="partner" />
        <main className="mx-auto w-full max-w-3xl px-4 py-8">
          <Link to="/partner/settings" className="inline-flex items-center gap-2 text-sm font-bold text-muted-foreground">
            <ArrowLeft className="h-4 w-4" /> Settings
          </Link>
          <section className="card-surface mt-6 p-6">
            <h1 className="font-display text-xl font-bold">Verification status unavailable</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              We couldn’t load your Partner verification record. No account information has been changed.
            </p>
            <Button variant="outline" className="mt-4" onClick={() => query.refetch()}>
              Try again
            </Button>
          </section>
        </main>
      </div>
    );
  }

  const { application, approved, hasPartnerRole } = query.data;
  const status = String(application?.status ?? "not_submitted");
  const statusLabel = status.replaceAll("_", " ");
  const StatusIcon =
    approved ? ShieldCheck :
    status === "rejected" || status === "suspended" ? ShieldAlert :
    status === "more_info_required" ? ShieldAlert :
    Clock3;

  return (
    <div className="min-h-screen bg-background pb-28">
      <AppHeader workspace="partner" />
      <main className="mx-auto w-full max-w-3xl px-4 py-8">
        <Link to="/partner/settings" className="inline-flex items-center gap-2 text-sm font-bold text-muted-foreground">
          <ArrowLeft className="h-4 w-4" /> Settings
        </Link>

        <header className="mt-6">
          <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-muted-foreground">Partner account</p>
          <h1 className="mt-1 text-3xl font-extrabold">Verification</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">
            This is the current status of the Partner application Shields uses for approval and marketplace access.
          </p>
        </header>

        <section className="card-surface mt-6 p-5">
          <div className="flex items-start gap-4">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary">
              <StatusIcon className="h-5 w-5" aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Current status</p>
              <h2 className="mt-1 text-xl font-extrabold capitalize">{approved ? "Approved Partner" : statusLabel}</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                {approved
                  ? "Your Partner application is approved and your Partner role is active."
                  : status === "more_info_required"
                    ? "Shields needs more information before a human reviewer can make a decision."
                    : status === "rejected"
                      ? "This application was not approved. Review any note below before deciding whether to apply again."
                      : status === "suspended"
                        ? "Your Partner status is suspended. Contact Shields support if you need help understanding the next step."
                        : application
                          ? "Your application is still being reviewed. Verification is never granted automatically."
                          : "No Partner application was found for this account."}
              </p>
            </div>
          </div>
        </section>

        {application && (
          <section className="mt-5 grid gap-3 sm:grid-cols-2">
            <Info label="Trading name" value={application.trading_name || "Not provided"} />
            <Info label="Company number" value={application.company_number || "Not provided"} />
            <Info label="Services" value={Array.isArray(application.services) && application.services.length ? application.services.join(", ") : "Not provided"} />
            <Info label="Operating areas" value={Array.isArray(application.operating_areas) && application.operating_areas.length ? application.operating_areas.join(", ") : "Not provided"} />
            <Info label="Insurance provider" value={application.insurance_provider || "Not provided"} />
            <Info label="Insurance expiry" value={application.insurance_expires || "Not provided"} />
          </section>
        )}

        {application?.reviewer_notes && (
          <section className="card-surface mt-5 p-5">
            <h2 className="font-display font-bold">Reviewer note</h2>
            <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">{application.reviewer_notes}</p>
          </section>
        )}

        <section className="mt-6 flex flex-wrap gap-2">
          {!application && (
            <Link to="/partner/apply" className="btn-hero inline-flex">
              Start Partner application
            </Link>
          )}
          {status === "more_info_required" && (
            <Link to="/partner/apply" className="btn-hero inline-flex">
              Update application
            </Link>
          )}
          <Link to="/partner/profile" className="btn-secondary-hero inline-flex">
            Business profile
          </Link>
          <Link to="/help" className="btn-secondary-hero inline-flex">
            Get support
          </Link>
        </section>

        <div className="mt-6 flex items-center gap-2 text-xs text-muted-foreground">
          {approved && hasPartnerRole ? <CheckCircle2 className="h-4 w-4 text-success" /> : <ShieldAlert className="h-4 w-4" />}
          <span>
            “Verified Partner” is shown only when the application is approved and the Partner role is active.
          </span>
        </div>
      </main>
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <article className="rounded-xl border border-border bg-card p-4">
      <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-sm font-semibold">{value}</p>
    </article>
  );
}
